import { type ReactNode, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useResetAllStreaks } from '../hooks/use-reset-streak';
import { Step1Content, Step2Content } from './bulk-reset-streaks-parts';

type Step = 'closed' | 'step1' | 'step2';

function useBulkResetState() {
  const [step, setStep] = useState<Step>('closed');
  const [typed, setTyped] = useState('');
  const close = (): void => {
    setStep('closed');
    setTyped('');
  };
  return { step, setStep, typed, setTyped, close };
}

type BodyProps = {
  step: Step;
  setStep: (s: Step) => void;
  typed: string;
  setTyped: (v: string) => void;
  close: () => void;
  fire: () => void;
  pending: boolean;
};

function ModalBody(p: BodyProps): ReactNode {
  if (p.step === 'step1')
    return (
      <Step1Content onContinue={() => p.setStep('step2')} onCancel={p.close} />
    );
  return (
    <Step2Content
      typed={p.typed}
      setTyped={p.setTyped}
      onConfirm={p.fire}
      onCancel={p.close}
      pending={p.pending}
    />
  );
}

function TriggerBtn({ onClick }: { onClick: () => void }): ReactNode {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="gap-1 hover:text-brand-red"
    >
      <RotateCcw size={14} /> Resetear streaks de TODOS
    </Button>
  );
}

function modalTitle(step: Step): string {
  return step === 'step1' ? 'Resetear streaks de TODOS' : 'Confirmación final';
}

export function BulkResetStreaksButton(): ReactNode {
  const s = useBulkResetState();
  const reset = useResetAllStreaks();
  const fire = (): void => {
    reset.mutate(undefined, { onSuccess: s.close });
  };
  const body = { ...s, close: s.close, fire, pending: reset.isPending };
  return (
    <>
      <TriggerBtn onClick={() => s.setStep('step1')} />
      <Modal
        open={s.step !== 'closed'}
        onClose={s.close}
        title={modalTitle(s.step)}
      >
        <ModalBody {...body} />
      </Modal>
    </>
  );
}
